import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export interface ConfirmDialogProps {
  /** Rendered while non-null; pass `null` to unmount (mount-per-open keeps state fresh). */
  isOpen: boolean;
  title: string;
  /** Body copy — string or richer content (e.g. a count line). */
  message: ReactNode;
  /** Primary button label (e.g. "Archive", "Restore", "Delete"). */
  confirmLabel: string;
  /** Style the primary button as destructive (red) — for Delete. */
  destructive?: boolean;
  /** The action is in flight — disables both buttons + shows a spinner on confirm. */
  isPending?: boolean;
  /** Disables ONLY the primary button, leaving Cancel and backdrop-dismiss live. For the window where the dialog is
   *  still resolving what the action will affect: the user must be able to back out, but must not be able to confirm
   *  an operation whose scope is not yet known (ADR-006 #108). Distinct from `isPending`, which means "committed". */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A small reusable confirmation modal over the {@link Dialog} primitive — the shared "are you sure?" for facility
 * Archive / Unarchive (and later Delete / Duplicate). Not dismissable while pending (so the action can't be abandoned
 * mid-flight); focus is trapped + restored by the primitive. Errors are surfaced by the caller via `useShowError`.
 */
export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  destructive = false,
  isPending = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      isOpen={isOpen}
      isDismissable={!isPending}
      onOpenChange={(open) => {
        if (!open && !isPending) onCancel();
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{message}</DialogDescription>
      <DialogFooter>
        <Button variant="ghost" onPress={onCancel} isDisabled={isPending}>
          {t("shell.cancel")}
        </Button>
        <Button variant={destructive ? "destructive" : "default"} onPress={onConfirm} isDisabled={isPending || confirmDisabled}>
          {isPending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
